/**
 * ResumeScorePage -- AI-powered resume scoring and feedback.
 *
 * Users paste their resume text and optionally a job description,
 * then get a 0-100 score with categorized feedback.
 */
import { useState } from 'react';
import { Panel } from '../../components/ui/Panel';
import { AiAssistButton } from '../../components/cms/AiAssistButton';
import { AiSuggestionPanel } from '../../components/cms/AiSuggestionPanel';
import { useAiAssist } from '../../hooks/useAiAssist';
import { aiApi } from '../../services/ai.api';
import type { ScoreResumeRequest, ScoreResumeData, ScoreFeedbackItem } from '../../types/ai.types';

function ScoreDisplay({ data }: { data: ScoreResumeData }) {
  const scoreColor =
    data.overall_score >= 75
      ? 'var(--color-success, #22c55e)'
      : data.overall_score >= 50
        ? 'var(--color-warning, #f59e0b)'
        : 'var(--color-danger, #ef4444)';

  const grouped: Record<string, ScoreFeedbackItem[]> = {
    strength: [],
    weakness: [],
    improvement: [],
  };
  for (const item of data.feedback) {
    (grouped[item.category] ??= []).push(item);
  }

  return (
    <div className="score-result">
      <div className="score-result__header">
        <div
          className="score-result__circle"
          style={{ borderColor: scoreColor }}
        >
          <span className="score-result__number" style={{ color: scoreColor }}>
            {data.overall_score}
          </span>
          <span className="score-result__label">/ 100</span>
        </div>
        <p className="score-result__summary">{data.summary}</p>
      </div>

      {Object.entries(grouped).map(
        ([category, items]) =>
          items.length > 0 && (
            <div key={category} className="score-result__category">
              <h4 className="score-result__category-title">
                {category === 'strength' && 'Strengths'}
                {category === 'weakness' && 'Weaknesses'}
                {category === 'improvement' && 'Improvements'}
              </h4>
              <ul className="score-result__list">
                {items.map((item, i) => (
                  <li key={i} className="score-result__item">
                    <span
                      className={`badge badge--${item.priority === 'high' ? 'danger' : item.priority === 'medium' ? 'warning' : 'active'}`}
                    >
                      {item.priority}
                    </span>
                    {item.item}
                  </li>
                ))}
              </ul>
            </div>
          ),
      )}

      {data.missing_keywords && data.missing_keywords.length > 0 && (
        <div className="score-result__keywords">
          <h4 className="score-result__category-title">Missing Keywords</h4>
          <div className="score-result__keyword-tags">
            {data.missing_keywords.map((kw, i) => (
              <span key={i} className="badge badge--outline">
                {kw}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export function ResumeScorePage() {
  const [resumeText, setResumeText] = useState('');
  const [jobDescription, setJobDescription] = useState('');
  const [targetRole, setTargetRole] = useState('');
  const [lastResult, setLastResult] = useState<ScoreResumeData | null>(null);

  const scoreAi = useAiAssist<ScoreResumeRequest, ScoreResumeData>({
    apiCall: (input) => aiApi.scoreResume(input),
    onAccept: (data) => {
      setLastResult(data);
    },
  });

  const canScore = resumeText.trim().length >= 50;

  return (
    <div className="cms-page">
      <header className="cms-page__header">
        <h1 className="cms-page__title">Score Resume</h1>
        <AiAssistButton
          size="md"
          onClick={() =>
            void scoreAi.trigger({
              resume_text: resumeText.trim(),
              job_description: jobDescription.trim() || undefined,
              target_role: targetRole.trim() || undefined,
            })
          }
          isLoading={scoreAi.isLoading}
          disabled={!canScore}
          label="Score my resume with AI"
        />
      </header>

      <Panel>
        <Panel.Header>Resume Text</Panel.Header>
        <Panel.Content>
          <div className="input-wrapper">
            <textarea
              className="input input--textarea"
              rows={10}
              value={resumeText}
              onChange={(e) => setResumeText(e.target.value)}
              placeholder="Paste your full resume text here (minimum 50 characters)..."
            />
          </div>
          <div className="experience-entry__row">
            <div className="input-wrapper" style={{ flex: 1 }}>
              <label className="input-label">Target Role (optional)</label>
              <input
                className="input"
                value={targetRole}
                onChange={(e) => setTargetRole(e.target.value)}
                placeholder="e.g. Senior Software Engineer"
              />
            </div>
          </div>
          <div className="input-wrapper">
            <label className="input-label">Job Description (optional)</label>
            <textarea
              className="input input--textarea"
              rows={5}
              value={jobDescription}
              onChange={(e) => setJobDescription(e.target.value)}
              placeholder="Paste a job description to get targeted scoring..."
            />
          </div>
        </Panel.Content>
      </Panel>

      <AiSuggestionPanel
        suggestion={scoreAi.suggestion as Record<string, unknown> | null}
        error={scoreAi.error}
        isLoading={scoreAi.isLoading}
        onAccept={scoreAi.accept}
        onDismiss={scoreAi.dismiss}
        renderSuggestion={(s) => <ScoreDisplay data={s as ScoreResumeData} />}
      />

      {lastResult && !scoreAi.suggestion && !scoreAi.isLoading && (
        <Panel>
          <Panel.Header>Last Score Result</Panel.Header>
          <Panel.Content>
            <ScoreDisplay data={lastResult} />
          </Panel.Content>
        </Panel>
      )}

      {!canScore && resumeText.length > 0 && (
        <p className="list-empty">
          Resume text must be at least 50 characters to score.
        </p>
      )}
    </div>
  );
}
